import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import {
  fetchSheetDiff,
  sheetImageUrl,
  type PaaSheetChange,
  type PaaSheetDiff,
} from "./api";
import {
  SheetPane,
  ZoomButton,
  isTypingInto,
  useSheetLayout,
  useSyncedPanes,
} from "./compare/panes";
import { PairingChoice, SameNumberPrompt } from "./review/SheetPairing";
import type { ApprovedSheet, Pairing } from "./review/pairing";

/** The pairing decision, wired. Named once: it is passed straight through to
 *  the decision bar, and two copies of a prop shape is how one of them grows a
 *  field the other never renders. */
interface PairingControls {
  state: Pairing;
  approvedSheets: readonly ApprovedSheet[];
  onChoose: (approvedSheetId: string | null) => void;
  onSettle: (choice: "updated" | "both") => void;
  onUnsettle: () => void;
  onDropRow: () => void;
}

// Side-by-side drawing viewer for one superseding sheet, with the differences
// marked.
//
// This is the ground truth behind a change note: reading "4th floor layout
// updated" tells you what the system believes, seeing the two drawings tells you
// whether it's right.
//
// THE DISPLAY PROBLEM. A plan sheet is already dense black line work, and a
// sheet can have twenty changes. Painting twenty filled highlight boxes over it
// produces confetti that hides the very detail the operator came to read — and
// at "fit" zoom the boxes are too small to see, while at 200% only one is on
// screen at all. So:
//
//   * the STEPPER is the primary interaction, not the markers. "Change 3 of 12",
//     arrow keys, and both panes scroll to centre it. Finding the difference
//     stops being a hunt.
//   * markers are thin OUTLINES, never fills. The drawing underneath stays
//     readable.
//   * ONE accent colour. Which pane you're looking at already tells you added vs
//     removed, so a red/green vocabulary would be redundant (and unreadable for
//     colour-blind users over black line work).
//   * every marker faint, the CURRENT one strong. Attention goes to one place.
//   * no looping animation. Drawings are busy enough.
//   * a toggle to hide them, because sometimes you just want the drawing.


export function SheetCompareViewer({
  compareId,
  sheet,
  baseline,
  note,
  reviewReason,
  sequence,
  pairing,
  onClose,
}: {
  compareId: string;
  /** Sheet id in the UPDATED set. */
  sheet: string;
  /** Sheet id in the APPROVED set — differs when the row overrides its
   *  baseline (renumbered / broken-out sheets). */
  baseline: string;
  /** The change note under review. Read-only unless `sequence.onNoteChange` is
   *  given, in which case this surface is where it gets edited. */
  note?: string | null;
  /** Why this sheet was flagged for a human, when it was. An operator who
   *  opened the viewer on a flagged sheet needs to know what the tool could not
   *  settle before they start looking, not after. */
  reviewReason?: string | null;
  /** Present when the viewer is being driven as a review pass rather than
   *  opened on one sheet. This is what turns it from something you open and
   *  close thirty-five times into the surface the work happens in. */
  sequence?: {
    /** 1-based, for "sheet 4 of 35". */
    index: number;
    total: number;
    reviewed: boolean;
    remaining: number;
    onPrevSheet: (() => void) | null;
    onNextSheet: (() => void) | null;
    /** Mark this sheet reviewed and move to the next one still outstanding. */
    onConfirm: () => void;
    onNoteChange: (next: string) => void;
    /** The note field lost focus — the edit was a considered one. */
    onNoteCommitted: () => void;
  };
  /** What this page supersedes, settled here as well as on the row. Both are the
   *  same decision and the same control — see review/SheetPairing. Absent when
   *  no read of the approved set is in hand to offer choices from. */
  pairing?: PairingControls;
  onClose: () => void;
}) {
  // Shared zoom. Plan sheets are 24x36 — "fit" orients you, 100%+ is where the
  // detail lives, so both are one click apart. Seeded from the remembered
  // layout, and written back whenever it changes.
  const { layout, patchLayout } = useSheetLayout();
  const { fit, zoom, showMarkers, collapsed } = layout;
  const setZoom = useCallback(
    // Reads the CURRENT zoom, not the one this render closed over — otherwise
    // quick repeated presses all compute the same step. See patchLayout.
    (f: (z: number) => number) =>
      patchLayout((l) => ({ fit: false, zoom: f(l.zoom) })),
    [patchLayout],
  );
  const setFit = useCallback((v: boolean) => patchLayout({ fit: v }), [patchLayout]);

  // Hold a key and the updated pane shows the approved drawing IN PLACE.
  //
  // Side by side makes the eye carry detail across a saccade, which is the one
  // thing it cannot do. Toggling in the same spot turns a difference into
  // apparent motion, which is detected pre-attentively — the flicker paradigm,
  // and the reason blink comparators outlived the people who built them. It is
  // a companion to the two panes, not a replacement: both staying visible is
  // what keeps the memory load low, and this is for settling one detail.
  const [blink, setBlink] = useState(false);

  const [diff, setDiff] = useState<PaaSheetDiff | null>(null);
  const [diffState, setDiffState] = useState<"loading" | "ready" | "failed">("loading");
  const [graphicsPending, setGraphicsPending] = useState(true);
  // -1 = nothing stepped to yet, so all markers render faint.
  const [current, setCurrent] = useState(-1);

  const { oldPaneRef, newPaneRef, syncing, syncScroll } = useSyncedPanes();

  // Load markers. The text pass is instant (the indexed tokens are already on
  // the server), so ask for it alone first and show markers straight away; then
  // upgrade with the graphics pass, which has to render both pages.
  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    setDiff(null);
    setDiffState("loading");
    setGraphicsPending(true);
    setCurrent(-1);

    (async () => {
      try {
        const text = await fetchSheetDiff(compareId, sheet, baseline, {
          graphics: false,
          signal: controller.signal,
        });
        if (cancelled) return;
        setDiff(text);
        setDiffState("ready");
      } catch {
        if (!cancelled) setDiffState("failed");
        return;
      }
      try {
        const full = await fetchSheetDiff(compareId, sheet, baseline, {
          graphics: true,
          signal: controller.signal,
        });
        if (!cancelled) setDiff(full);
      } catch {
        // Keep the exact text markers we already have; only the graphics pass
        // is missing, and the footnote reports that honestly.
      } finally {
        if (!cancelled) setGraphicsPending(false);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [compareId, sheet, baseline]);

  const changes = diff?.changes ?? [];

  // Centre a change in BOTH panes. This is what makes twenty changes navigable
  // instead of overwhelming — the user never hunts for the marker.
  const goTo = useCallback(
    (idx: number) => {
      if (changes.length === 0) return;
      const next = ((idx % changes.length) + changes.length) % changes.length;
      setCurrent(next);
      const change = changes[next];

      syncing.current = true;
      for (const [pane, box] of [
        [oldPaneRef, change.old],
        [newPaneRef, change.new],
      ] as const) {
        const el = pane.current;
        const img = el?.querySelector("img");
        if (!el || !img || !box) continue;
        const cx = (box.x + box.width / 2) * img.clientWidth;
        const cy = (box.y + box.height / 2) * img.clientHeight;
        el.scrollTo({
          left: cx - el.clientWidth / 2,
          top: cy - el.clientHeight / 2,
          behavior: "smooth",
        });
      }
      // Released after the smooth scroll settles, so the two panes' scroll
      // handlers don't fight each other mid-animation.
      window.setTimeout(() => {
        syncing.current = false;
      }, 450);
    },
    [changes],
  );

  useEffect(() => {
    // Once this surface holds a text field, a bare letter binding is a bug: "n"
    // typed into the note would advance the sheet and lose the keystroke. Only
    // Escape survives a focused field, and Escape blurs rather than closing so
    // it takes two presses to leave a half-typed note.
    const typing = isTypingInto;

    const onKey = (e: KeyboardEvent) => {
      if (typing(e.target)) {
        if (e.key === "Escape") (e.target as HTMLElement).blur();
        return;
      }
      // Leave browser and OS combinations alone.
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      switch (e.key) {
        case "Escape":
          onClose();
          return;
        // The inner loop: the changes ON this sheet. Unchanged bindings —
        // whatever muscle memory exists is for these.
        case "ArrowRight":
        case "j":
          e.preventDefault();
          goTo(current + 1);
          return;
        case "ArrowLeft":
        case "k":
          e.preventDefault();
          goTo(current - 1);
          return;
        default:
          break;
      }

      if (!sequence) return;

      // The outer loop: between sheets.
      if (e.key === "n" && sequence.onNextSheet) {
        e.preventDefault();
        sequence.onNextSheet();
      } else if (e.key === "p" && sequence.onPrevSheet) {
        e.preventDefault();
        sequence.onPrevSheet();
      } else if (e.key === "Enter") {
        e.preventDefault();
        sequence.onConfirm();
      } else if (e.key === "b" && !e.repeat) {
        // Held, not toggled. A momentary swap is a comparison; a toggle is two
        // decisions and a question about which one you are looking at.
        e.preventDefault();
        setBlink(true);
      }
    };

    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === "b") setBlink(false);
    };
    // A lost window mid-hold would otherwise leave the panes swapped, which
    // silently shows the approved drawing labelled as the updated one.
    const onBlur = () => setBlink(false);

    window.addEventListener("keydown", onKey);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onBlur);
    // The drawings need the whole window; don't let the page scroll behind.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onBlur);
      document.body.style.overflow = prev;
    };
  }, [onClose, goTo, current, sequence]);

  const differentBaseline = baseline.toUpperCase() !== sheet.toUpperCase();
  const active = current >= 0 ? changes[current] : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Approved and updated ${sheet} side by side`}
      className="fixed inset-0 z-50 flex flex-col bg-ink/70 backdrop-blur-sm"
    >
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-rule bg-surface px-5 py-3">
        <div className="flex min-w-0 items-baseline gap-3">
          {sequence && (
            <SheetNav
              index={sequence.index}
              total={sequence.total}
              onPrev={sequence.onPrevSheet}
              onNext={sequence.onNextSheet}
            />
          )}
          <span className="font-mono text-[1rem] font-medium text-ink">{sheet}</span>
          <span className="text-[0.85rem] text-ink-muted">
            approved vs updated
            {differentBaseline && (
              <> · baseline <span className="font-mono">{baseline}</span></>
            )}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Stepper
            state={diffState}
            count={changes.length}
            current={current}
            graphicsPending={graphicsPending}
            showMarkers={showMarkers}
            onToggleMarkers={() => patchLayout({ showMarkers: !showMarkers })}
            onPrev={() => goTo(current - 1)}
            onNext={() => goTo(current + 1)}
          />
          <span className="mx-1 h-5 w-px bg-rule" aria-hidden />
          {/* Fold either drawing away. Useful in both directions: an "N" sheet
              has no approved side worth the space, and a dense sheet is easier
              read one pane wide. */}
          <ZoomButton
            active={collapsed === "old"}
            onClick={() =>
              patchLayout({ collapsed: collapsed === "old" ? null : "old" })
            }
            title="Hide the approved drawing"
          >
            ◧
          </ZoomButton>
          <ZoomButton
            active={collapsed === "new"}
            onClick={() =>
              patchLayout({ collapsed: collapsed === "new" ? null : "new" })
            }
            title="Hide the updated drawing"
          >
            ◨
          </ZoomButton>
          <button
            type="button"
            onMouseDown={() => setBlink(true)}
            onMouseUp={() => setBlink(false)}
            onMouseLeave={() => setBlink(false)}
            title="Hold to show the approved drawing in the updated pane (or hold B)"
            className={cn(
              "h-7 rounded-md border px-2 text-[0.8rem] font-medium transition-colors",
              blink
                ? "border-primary bg-primary-soft text-primary-ink"
                : "border-rule-strong bg-surface text-ink-soft hover:border-primary hover:text-ink",
            )}
          >
            Blink
          </button>
          <span className="mx-1 h-5 w-px bg-rule" aria-hidden />
          <ZoomButton active={fit} onClick={() => setFit(true)}>
            Fit
          </ZoomButton>
          <ZoomButton
            active={!fit}
            onClick={() => {
              setFit(false);
              setZoom((z) => Math.max(0.25, z - 0.25));
            }}
            aria-label="Zoom out"
          >
            −
          </ZoomButton>
          <span className="w-12 text-center text-[0.8rem] tabular-nums text-ink-muted">
            {fit ? "fit" : `${Math.round(zoom * 100)}%`}
          </span>
          <ZoomButton
            active={!fit}
            onClick={() => {
              setFit(false);
              setZoom((z) => Math.min(6, z + 0.25));
            }}
            aria-label="Zoom in"
          >
            +
          </ZoomButton>
          <Button intent="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </header>

      {/* The stepped change, quoted. Cheaper to read than squinting at the
          drawing, and it makes the marker's meaning unambiguous. */}
      {active && <ChangeCaption change={active} />}

      {reviewReason?.trim() && !active && (
        <div className="flex items-start gap-2 border-b border-rule bg-warn-soft px-5 py-2 text-[0.85rem] leading-relaxed text-ink-soft">
          <span aria-hidden className="mt-px shrink-0">
            ⚠️
          </span>
          <span className="min-w-0">
            <span className="font-medium text-ink">Needs your eye. </span>
            {reviewReason}
          </span>
        </div>
      )}

      {/* Read-only note, for the case where this is still just a viewer. When
          it is driving a review pass the note is editable and lives in the bar
          BELOW the drawings instead — see DecisionBar. */}
      {!sequence && note?.trim() && !active && (
        <div className="border-b border-rule bg-lilac-soft px-5 py-2 text-[0.85rem] text-lilac-ink">
          <span className="label mr-2 text-lilac-ink/70">Note</span>
          {note}
        </div>
      )}

      <div
        className={cn(
          "grid min-h-0 flex-1 gap-px bg-rule",
          collapsed ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2",
        )}
      >
        {collapsed !== "old" && (
          <SheetPane
            ref={oldPaneRef}
            label="Approved"
            url={sheetImageUrl(compareId, baseline, "old")}
            fit={fit}
            zoom={zoom}
            changes={showMarkers ? changes : []}
            side="old"
            current={current}
            onPickChange={goTo}
            onScroll={() => syncScroll("old")}
          />
        )}
        {collapsed !== "new" && (
          <SheetPane
            ref={newPaneRef}
            label={blink ? "Approved (blinking)" : "Updated"}
            accent={!blink}
            url={sheetImageUrl(compareId, sheet, "new")}
            // Loaded up front and held at zero opacity, so the swap is instant.
            // Fetching it on first press would flash white at the exact moment
            // she is looking for a difference.
            blinkUrl={sheetImageUrl(compareId, baseline, "old")}
            blink={blink}
            fit={fit}
            zoom={zoom}
            changes={showMarkers ? changes : []}
            side="new"
            current={current}
            onPickChange={goTo}
            onScroll={() => syncScroll("new")}
          />
        )}
      </div>

      {sequence && (
        <DecisionBar
          note={note ?? ""}
          reviewed={sequence.reviewed}
          remaining={sequence.remaining}
          pairing={pairing}
          onNoteChange={sequence.onNoteChange}
          onNoteCommitted={sequence.onNoteCommitted}
          onConfirm={sequence.onConfirm}
        />
      )}

      <DiffFootnote diff={diff} graphicsPending={graphicsPending} state={diffState} />
    </div>
  );
}

// Header control: how many changes, which one you're on, and the way through
// them. Deliberately the most prominent thing after the sheet id.
function Stepper({
  state,
  count,
  current,
  graphicsPending,
  showMarkers,
  onToggleMarkers,
  onPrev,
  onNext,
}: {
  state: "loading" | "ready" | "failed";
  count: number;
  current: number;
  graphicsPending: boolean;
  showMarkers: boolean;
  onToggleMarkers: () => void;
  onPrev: () => void;
  onNext: () => void;
}) {
  if (state === "loading") {
    return (
      <span className="inline-flex items-center gap-2 text-[0.8rem] text-ink-muted">
        <span
          aria-hidden
          className="h-3 w-3 animate-spin rounded-full border-2 border-rule border-t-primary"
        />
        finding changes…
      </span>
    );
  }
  if (state === "failed") return null;
  if (count === 0) {
    return (
      <span className="text-[0.8rem] text-ink-muted">
        No differences found{graphicsPending ? " yet…" : ""}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1">
      <button
        type="button"
        onClick={onToggleMarkers}
        title={showMarkers ? "Hide the change markers" : "Show the change markers"}
        className={cn(
          "mr-1 rounded-md border px-2 py-1 text-[0.78rem] font-medium transition-colors",
          showMarkers
            ? "border-lilac/50 bg-lilac-soft text-lilac-ink"
            : "border-rule-strong bg-surface text-ink-muted hover:text-ink",
        )}
      >
        {count} change{count === 1 ? "" : "s"}
        {graphicsPending && "…"}
      </button>
      <button
        type="button"
        onClick={onPrev}
        aria-label="Previous change"
        title="Previous change (←)"
        className="h-7 w-7 rounded-md border border-rule-strong bg-surface text-[0.8rem] text-ink-soft transition-colors hover:border-primary hover:text-ink"
      >
        ‹
      </button>
      <span className="w-14 text-center text-[0.8rem] tabular-nums text-ink-soft">
        {current < 0 ? `– / ${count}` : `${current + 1} / ${count}`}
      </span>
      <button
        type="button"
        onClick={onNext}
        aria-label="Next change"
        title="Next change (→)"
        className="h-7 w-7 rounded-md border border-rule-strong bg-surface text-[0.8rem] text-ink-soft transition-colors hover:border-primary hover:text-ink"
      >
        ›
      </button>
    </span>
  );
}

// Where she is in the filing, and the way through it. Left of the sheet id,
// because it answers "how much is left" before "what am I looking at".
function SheetNav({
  index,
  total,
  onPrev,
  onNext,
}: {
  index: number;
  total: number;
  onPrev: (() => void) | null;
  onNext: (() => void) | null;
}) {
  return (
    <div className="flex shrink-0 items-center gap-1">
      <ZoomButton onClick={onPrev ?? undefined} disabled={!onPrev} aria-label="Previous sheet (P)">
        ‹
      </ZoomButton>
      <span className="w-20 text-center text-[0.8rem] tabular-nums text-ink-muted">
        {index} of {total}
      </span>
      <ZoomButton onClick={onNext ?? undefined} disabled={!onNext} aria-label="Next sheet (N)">
        ›
      </ZoomButton>
    </div>
  );
}

// The decision, under the evidence.
//
// It is a full-width BAR rather than a middle column for a reason that is
// arithmetic: a plan sheet is 3:2 landscape, and three columns on a laptop
// leaves each drawing about 500px wide — too small to read the title-block
// level labels, which is precisely what the operator goes to the drawing for.
// Her own edits are transcriptions off the sheet ("2nd to 5th floor plan
// updated"), so illegible drawings fail the primary task. A note is 110
// characters: it wants width and almost no height. So the note gets the width
// and the drawings keep the height.
function DecisionBar({
  note,
  reviewed,
  remaining,
  pairing,
  onNoteChange,
  onNoteCommitted,
  onConfirm,
}: {
  note: string;
  reviewed: boolean;
  remaining: number;
  pairing?: PairingControls;
  onNoteChange: (next: string) => void;
  onNoteCommitted: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="shrink-0 border-t border-rule bg-surface px-5 py-3">
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-[20rem] flex-[2]">
          <label
            htmlFor="paa-review-note"
            className="label mb-1 block text-ink-muted"
          >
            What changed on this sheet
          </label>
          <textarea
            id="paa-review-note"
            rows={2}
            value={note}
            onChange={(e) => onNoteChange(e.target.value)}
            onBlur={onNoteCommitted}
            placeholder="What changed on this sheet"
            className="w-full resize-none rounded-md border border-rule bg-canvas px-3 py-2 text-[0.9rem] leading-snug text-ink focus:border-primary focus:outline-none focus:shadow-ring"
          />
        </div>

        {/* What it supersedes, beside the sentence about it. The two decisions
            are made off the same pair of drawings, so asking them in two places
            was asking her to look at every page twice. */}
        {pairing && (
          <div className="min-w-[14rem] flex-1 basis-[16rem] pt-[1.35rem]">
            <PairingChoice
              pairing={pairing.state}
              approvedSheets={pairing.approvedSheets}
              onChoose={pairing.onChoose}
            />
          </div>
        )}

        <div className="flex shrink-0 flex-col items-end gap-1.5 pt-5">
          <Button intent={reviewed ? "secondary" : "primary"} onClick={onConfirm}>
            {reviewed ? "Confirmed · next" : "Confirm · next"}
          </Button>
          <span className="text-[0.74rem] tabular-nums text-ink-muted">
            {remaining === 0
              ? "every sheet confirmed"
              : `${remaining} still to confirm`}
          </span>
        </div>
      </div>

      {/* Full width and never folded away: a question with no default, about
          the one outcome that would put the same drawing in a filing twice. */}
      {pairing && (
        <SameNumberPrompt
          pairing={pairing.state}
          onSettle={pairing.onSettle}
          onUnsettle={pairing.onUnsettle}
          onDropRow={pairing.onDropRow}
        />
      )}
    </div>
  );
}

// What the current change actually is. A text change can quote itself; a
// graphics region can only say where it is.
function ChangeCaption({ change }: { change: PaaSheetChange }) {
  if (change.kind === "Graphics") {
    return (
      <div className="flex items-center gap-2 border-b border-rule bg-lilac-soft px-5 py-2 text-[0.85rem] text-lilac-ink">
        <span className="label shrink-0 text-lilac-ink/70">Drawing</span>
        <span>
          The line work differs here. Nothing on this spot changed in text, so
          compare the two panes directly.
        </span>
      </div>
    );
  }
  const from = change.oldText?.trim();
  const to = change.newText?.trim();
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-rule bg-lilac-soft px-5 py-2 text-[0.85rem] text-lilac-ink">
      {from && (
        <span className="min-w-0">
          <span className="label mr-1.5 text-lilac-ink/70">Was</span>
          <span className="line-through decoration-lilac-ink/40">{from}</span>
        </span>
      )}
      {to && (
        <span className="min-w-0">
          <span className="label mr-1.5 text-lilac-ink/70">Now</span>
          <span className="font-medium">{to}</span>
        </span>
      )}
      {!from && to && <span className="text-lilac-ink/70">(added)</span>}
      {from && !to && <span className="text-lilac-ink/70">(removed)</span>}
    </div>
  );
}

// The honest footnote. Without it, "no marker here" reads as "nothing changed
// here" — which is wrong on a graphics-only revision and would poison trust in
// every other marker on the sheet.
function DiffFootnote({
  diff,
  graphicsPending,
  state,
}: {
  diff: PaaSheetDiff | null;
  graphicsPending: boolean;
  state: "loading" | "ready" | "failed";
}) {
  if (state === "failed") {
    return (
      <footer className="border-t border-rule bg-surface px-5 py-1.5 text-[0.78rem] text-ink-muted">
        Change markers unavailable — compare the drawings directly.
      </footer>
    );
  }
  if (!diff) return null;

  const bits: string[] = [];
  if (diff.textChangeCount > 0) {
    bits.push(`${diff.textChangeCount} text change${diff.textChangeCount === 1 ? "" : "s"} (exact)`);
  }
  if (graphicsPending) {
    bits.push("checking the drawings for changes…");
  } else if (diff.graphicsComparisonSkipped) {
    bits.push(diff.graphicsSkipReason ?? "Graphics comparison skipped.");
  } else if (diff.graphicsChangeCount > 0) {
    bits.push(
      `${diff.graphicsChangeCount} drawing area${diff.graphicsChangeCount === 1 ? "" : "s"} (approximate)`,
    );
  } else {
    bits.push("no other drawing changes detected");
  }
  if (diff.changesOmitted > 0) {
    bits.push(`${diff.changesOmitted} more not shown`);
  }

  return (
    <footer className="border-t border-rule bg-surface px-5 py-1.5 text-[0.78rem] text-ink-muted">
      {bits.join(" · ")}
    </footer>
  );
}


